import React, { useEffect, useMemo, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import { Swiper, SwiperSlide } from 'swiper/react';
import SwiperCore, { Pagination, Navigation } from 'swiper/core';
import 'swiper/swiper-bundle.min.css';
import 'swiper/swiper.min.css';
import { toast } from 'react-toastify';
import { Button, Modal } from 'react-bootstrap';
import { useQuery, useQueryClient } from 'react-query';
import Select from 'react-select';
import Moment from 'react-moment';
import { useDispatch } from 'react-redux';
import moment from 'moment-timezone';
import {
  Map,
  MapPin,
  Navigation as NavigationIcon,
  Heart,
  Image,
  Share2,
  UserPlus,
  FileText,
  Calendar,
  Clock,
} from 'react-feather';
import CenteredSpinner from '../CenteredSpinner';
import { singlePropertyMap } from '../../redux/action';
import * as AppConstants from '../../AppConstants';
import { getSysSetting, numberWithCommas } from '../../Utils';
import noImage from '../../images/noimage.png';
import { UpdateNextAvailableDateModal } from './UpdateNextAvailableDateModal';
import { OwnerLastContactModal } from './OwnerLastContactModal';
import { UpdateMarketStatusModal } from './UpdateMarketStatusModal';

SwiperCore.use([Pagination, Navigation]);

export const PropertyActionsModal = ({ show, propertyId, property, onClose, originPath }) => {
  const navigate = useNavigate();
  const dispatch = useDispatch();
  const queryClient = useQueryClient();
  const [propertyID, setPropertyID] = useState(propertyId ?? property?.id ?? null);
  const [showFavouriteModal, setShowFavouriteModal] = useState(false);
  const [showPriceModal, setShowPriceModal] = useState(false);
  const [showCoordinatesModal, setShowCoordinatesModal] = useState(false);
  const [showUploadModal, setShowUploadModal] = useState(false);
  const [showUpdateOwnerLastContactModal, setShowUpdateOwnerLastContactModal] = useState(false);
  const [showUpdateOwnerLastContactModalQuestion, setShowUpdateOwnerLastContactModalQuestion] = useState(false);
  const [showNextAvailableModal, setShowNextAvailableModal] = useState(false);
  const [showMarketStatusModal, setShowMarketStatusModal] = useState(false);
  const [reason, setReason] = useState(null);
  const [reasonError, setReasonError] = useState(null);
  const [comment, setComment] = useState(null);
  const [commentError, setCommentError] = useState(null);
  const [ownerContactDate, setOwnerContactDate] = useState(null);
  const [ownerContactDateError, setOwnerContactDateError] = useState(null);
  const [nextAvailableDate, setNextAvailableDate] = useState(null);
  const [nextAvailableError, setNextAvailableError] = useState(null);
  const [noteTextError, setNoteTextError] = useState(null);
  const [noteText, setNoteText] = useState(null);
  const [disabled, setDisabled] = useState(false);
  const [currentLat, setCurrentLat] = useState(null);
  const [currentLon, setCurrentLon] = useState(null);
  const [price, setPrice] = useState(null);
  const [images, setImages] = useState(null);
  const [errorImages, setErrorImages] = useState(null);
  const [multipleFiles, setMulitpleFiles] = useState(null);
  const [mountRefImages, setMountRefImages] = useState(null);
  const refImage = useRef(null);

  const path = useMemo(() => {
    if (originPath) return originPath;
    try {
      return JSON.parse(localStorage.getItem('Path'));
    } catch {
      return localStorage.getItem('Path');
    }
  }, [originPath]);

  useEffect(() => {
    setPropertyID(propertyId ?? property?.id ?? null);
  }, [propertyId, property]);

  useEffect(() => {
    if (!show) return;
    const token = localStorage.getItem('token');
    if (token) {
      axios.defaults.headers.common['Authorization'] = `Bearer ${token}`;
    }
  }, [show]);

  const fetchPropertyDetailsByID = (id) => {
    const options = {
      method: 'GET',
      url: `/property/getsummary/${id}`,
    };

    return axios
      .request(options)
      .then((response) => {
        const options2 = {
          method: 'GET',
          url: `/externalfile/GetByRecordContextType/${id}/3/1`,
        };

        return axios
          .request(options2)
          .then((response2) => {
            response.data.externalFiles = response2.data;
            return response.data;
          })
          .catch(() => response.data);
      })
      .catch(() => null);
  };

  const {
    isLoading: isLoadingPropertyDetails,
    data: dataPropertyDetails,
    refetch,
  } = useQuery(['propertydetails', propertyID], () => fetchPropertyDetailsByID(propertyID), {
    enabled: Boolean(show && propertyID),
  });

  useEffect(() => {
    if (!dataPropertyDetails) return;
    setOwnerContactDate(
      dataPropertyDetails?.ownerLastContacted ? moment(dataPropertyDetails.ownerLastContacted).toDate() : null,
    );
    setNextAvailableDate(
      dataPropertyDetails?.propertyToLet?.lettingNextAvail
        ? moment(dataPropertyDetails.propertyToLet.lettingNextAvail).toDate()
        : null,
    );
  }, [dataPropertyDetails]);

  const propertyData = useMemo(() => dataPropertyDetails ?? property, [dataPropertyDetails, property]);

  const fetchReason = () => {
    const options = {
      method: 'GET',
      url: `/propertyfavouritereason`,
    };

    return axios
      .request(options)
      .then((response) => response.data.map((element) => ({ value: element.id, label: element.description })))
      .catch(() => []);
  };

  const { data: reasonData } = useQuery(['Reason'], fetchReason, {
    refetchOnWindowFocus: false,
  });

  useEffect(() => {
    if (!reasonData) return;
    localStorage.setItem('ReasonData', JSON.stringify(reasonData));
  }, [reasonData]);

  const reasonOptions = useMemo(() => {
    if (reasonData) return reasonData;
    try {
      return JSON.parse(localStorage.getItem('ReasonData')) || [];
    } catch {
      return [];
    }
  }, [reasonData]);

  const handleReasonChange = (event) => {
    setReasonError(null);
    setReason(event.value);
  };

  const handleComment = (e) => {
    setCommentError(null);
    setComment(e.target.value);
  };

  const handlePriceAutoComplete = () => {
    let priceMoney;
    if (price !== null && price !== '') {
      priceMoney = price
        .replace(/[ ,.]/g, '')
        .replace(/[K|k]/g, '*1000')
        .replace(/[M|m]/g, '*1000000')
        .replace(/[^0-9/*.]/g, '');

      var args = priceMoney.split('*');
      priceMoney = args.reduce((a, b) => a * b);
      let returnVal = numberWithCommas(priceMoney);
      setPrice(returnVal);
      return;
    }
    setPrice('');
  };

  const handlePriceChange = (e) => {
    setPrice(e.target.value.trim());
  };

  const handleUpdateOwnerLastContact = async (e) => {
    setDisabled(true);
    e.preventDefault();
    e.stopPropagation();
    if (!noteText) {
      setNoteTextError("Note Text input can't be empty");
      setDisabled(false);
      return;
    }

    const config = {
      method: 'POST',
      url: '/property/updateownerlastcontact',
      headers: {
        'Content-Type': 'application/json',
      },
      data: JSON.stringify({
        propertyId: propertyID,
        lastContactDate: ownerContactDate,
        noteText: noteText,
        staffMemberId: localStorage.getItem('staffMemberId'),
        username: localStorage.getItem('username'),
      }),
    };

    try {
      const data = await axios(config);

      if (data.status === 200) {
        setNoteText(null);
        setDisabled(false);
        toast.success(`Owner last contact date has been updated.`, {
          position: 'top-center',
          autoClose: 3000,
          hideProgressBar: false,
          draggable: true,
          progress: undefined,
        });
        refetch();
      }
    } catch (err) {
      setDisabled(false);
      toast.error(`Sorry, property wasn't added, try again ${err.message}`, {
        position: 'top-left',
        autoClose: 3000,
        hideProgressBar: false,
        closeOnClick: true,
        draggable: true,
        progress: undefined,
      });
    }
    setReason(null);
    setComment(null);
    setShowFavouriteModal(false);

    setShowUpdateOwnerLastContactModal(false);
  };

  const handleUpdateOwnerLastContactQuestion = async (e) => {
    e.preventDefault();
    e.stopPropagation();

    let d = new Date();
    let isoDate = d.toLocaleString('en-US');

    const config = {
      method: 'POST',
      url: '/property/updateownerlastcontact',
      headers: {
        'Content-Type': 'application/json',
      },
      data: JSON.stringify({
        propertyId: propertyID,
        lastContactDate: isoDate,
        noteText: 'Updated by price or availability date change.',
        staffMemberId: localStorage.getItem('staffMemberId'),
        username: localStorage.getItem('username'),
      }),
    };

    try {
      const data = await axios(config);

      if (data.status === 200) {
        setDisabled(false);
        toast.success(`Property last contact date has been updated`, {
          position: 'top-center',
          autoClose: 3000,
          hideProgressBar: false,
          draggable: true,
          progress: undefined,
        });
        refetch();
      }
    } catch (err) {
      setDisabled(false);
      toast.error(`Error ${err.message}`, {
        position: 'top-center',
        autoClose: 3000,
        hideProgressBar: false,
        closeOnClick: true,
        draggable: true,
        progress: undefined,
      });
    }
    setShowUpdateOwnerLastContactModalQuestion(false);
  };

  const handleUpdatePropertyCoordinates = async () => {
    const config = {
      method: 'POST',
      url: '/Property/UpdatePropertyCoordinates',
      headers: {
        'Content-Type': 'application/json',
      },
      data: JSON.stringify({
        Id: propertyID,
        GoogleMapLatitude: currentLat,
        GoogleMapLongitude: currentLon,
        UsernameSubmitted: localStorage.getItem('username'),
      }),
    };

    try {
      const data = await axios(config);

      if (data.status === 200) {
        toast.success(`Property Coordinates Updated`, {
          position: 'top-center',
          autoClose: 3000,
          hideProgressBar: false,
          draggable: true,
          progress: undefined,
        });
        refetch();
      }
    } catch (err) {
      setDisabled(false);
      toast.error(`We are still determining your coordinates, please try again.`, {
        position: 'top-center',
        autoClose: 3000,
        hideProgressBar: false,
        closeOnClick: true,
        draggable: true,
        progress: undefined,
      });
    }
    setShowCoordinatesModal(false);
  };

  const handleUpdatePropertyPrice = async () => {
    const config = {
      method: 'POST',
      url: '/Property/UpdatePropertyPrice',
      headers: {
        'Content-Type': 'application/json',
      },
      data: JSON.stringify({
        Id: propertyID,
        PriceSQ: price,
        AmendmentByStaffId: localStorage.getItem('staffMemberId'),
        UsernameSubmitted: localStorage.getItem('username'),
      }),
    };

    try {
      const data = await axios(config);

      if (data.status === 200) {
        toast.success(`Property Price Updated`, {
          position: 'top-center',
          autoClose: 3000,
          hideProgressBar: false,
          draggable: true,
          progress: undefined,
        });
        refetch();
        setShowUpdateOwnerLastContactModalQuestion(true);
      }
    } catch (err) {
      // handled by toast
    }
    setShowPriceModal(false);
  };

  const toBase64 = (file) => {
    return new Promise((resolve, reject) => {
      const reader = new FileReader();
      reader.readAsDataURL(file);
      reader.onload = () => resolve({ data: reader.result, filename: file.name });
      reader.onerror = (error) => reject(error);
    });
  };

  const tobase64Handler = async (files) => {
    const filePathsPromises = [];
    Array.from(files).forEach((file) => {
      filePathsPromises.push(toBase64(file));
    });
    const filePaths = await Promise.all(filePathsPromises);
    return filePaths;
  };

  const handleDeleteImage = (id) => {
    setImages((oldState) => oldState.filter((item, index) => index !== id));

    refImage.current.files = mountRefImages;

    const dt = new DataTransfer();
    Array.from(refImage.current.files).forEach((filev, index) => {
      if (id !== index) {
        dt.items.add(new File([filev], filev.name, { type: filev.type }));
      }
    });

    let fileObj = [];

    fileObj.push(dt.files);

    setMountRefImages(dt.files);

    if (fileObj < 0) {
      return;
    }

    tobase64Handler(dt.files).then((data) => {
      setMulitpleFiles(data);
    });

    refImage.current.files = dt.files;
  };

  const handleUploadMultipleFiles = (e) => {
    e.persist();
    e.preventDefault();
    let fileObj = [];
    let fileArray = [];

    refImage.current.files = e.target.files ?? mountRefImages;

    fileObj.push(e.target.files);
    for (let i = 0; i < fileObj[0].length; i++) {
      if (fileObj[0][i].type !== 'image/jpeg') {
        setErrorImages((prevState) => ({
          ...prevState,
          type: 'Only images can be uploaded',
        }));
      }
      if (fileObj[0][i].size >= 10485760) {
        setErrorImages((prevState) => ({
          ...prevState,
          size: 'File too Big, please select a file less than 10mb',
        }));
      }
      fileArray.push(URL.createObjectURL(fileObj[0][i]));
    }

    setImages(fileArray);

    if (e.target.files < 0 || !e.target.validity.valid) {
      return;
    }

    tobase64Handler(e.target.files).then((data) => {
      setMulitpleFiles(data);
    });

    setMountRefImages(e.target.files);
  };

  const handleUploadPropertyImages = async () => {
    let ftc = propertyData?.propertyRef?.substring(0, 2);
    let ffc = propertyData?.propertyRef?.substring(0, 4);
    if (disabled === true) return false;

    setDisabled(true);

    if (multipleFiles !== null) {
      const payload = await Promise.all(
        multipleFiles.map(async (file) => {
          var base64Image = file.data.replace(/^data:image\/[^;]+;base64,/, '');

          var dotIndex = file.filename.lastIndexOf('.');
          var ext = file.filename.substring(dotIndex).toUpperCase().replace(/\./g, '');

          var imagePath = `Property\\P${ftc}\\P${ffc}\\P${propertyData?.propertyRef}\\O_${propertyData?.propertyRef}_${file.filename}`;

          var ImagesRequestOptions = {
            method: 'POST',
            url: `/property/createanduploadpropertyextfile`,
            headers: {
              'Content-Type': 'application/json',
            },
            data: JSON.stringify({
              Base64File: `${base64Image}`,
              MetaData: {
                RecordId: 0,
                FileType: 1,
                Rating: 0,
                SequenceNo: 99,
                FileExtension: `${ext}`,
                FilePath: `${imagePath}`,
                Description: `Image Uploaded From Mobile`,
              },
              FileName: `${file.filename}.jpg`,
              PropertyRef: `${propertyData?.propertyRef}`,
            }),
          };

          try {
            return await axios(ImagesRequestOptions);
          } catch (err) {
            return null;
          }
        }),
      );

      if (payload) {
        toast.success(`Images Uploaded Successfully`, {
          position: 'top-center',
          autoClose: 3000,
          hideProgressBar: false,
          closeOnClick: true,
          pauseOnHover: true,
          draggable: true,
          progress: undefined,
        });
      }
      setDisabled(false);
      setShowUploadModal(false);
    }
  };

  const addFavourite = async (e, propertyRef) => {
    setDisabled(true);
    if (!reason) {
      setReasonError("Reason input can't be emptied");
      setDisabled(false);
      return;
    }
    if (!comment) {
      setCommentError("Comment input can't be emptied");
      setDisabled(false);
      return;
    }

    e.preventDefault();
    e.stopPropagation();
    queryClient.invalidateQueries('Favourites');

    let d = new Date();
    let isoDate = d.toLocaleString('en-US');

    const config = {
      method: 'POST',
      url: '/propertyfavourite',
      headers: {
        'Content-Type': 'application/json',
      },
      data: JSON.stringify({
        propertyId: propertyID,
        sys_UserId: localStorage.getItem('userId'),
        dateRegistered: isoDate,
        propertyFavouriteReasonId: reason,
        comments: comment,
      }),
    };

    try {
      const data = await axios(config);
      if (data.status === 201) {
        setReason(null);
        setComment(null);
        setDisabled(false);
        toast.success(`Property: ${propertyRef} has been added`, {
          position: 'top-center',
          autoClose: 3000,
          hideProgressBar: false,
          closeOnClick: true,
          draggable: true,
          progress: undefined,
        });
      }
    } catch (err) {
      if (err)
        toast.error(`Sorry, property wasn't added, try again ${err.message}`, {
          position: 'top-left',
          autoClose: 3000,
          hideProgressBar: false,
          closeOnClick: true,
          draggable: true,
          progress: undefined,
        });
    }
    setReason(null);
    setComment(null);
    setShowFavouriteModal(false);
  };

  const handleMap = (details, searchPath) => {
    navigate('/MapPerCard');
    dispatch(
      singlePropertyMap({
        property: details,
        searchPath: searchPath,
      }),
    );
    if (onClose) onClose();
  };

  const handleAddBuyerProperties = () => {
    navigate('/AddBuyerProperties', {
      state: {
        propertyId: propertyID,
      },
    });
    if (onClose) onClose();
  };

  const handleUpdateNextAvailableDate = async (e) => {
    setDisabled(true);
    if (!nextAvailableDate) {
      setNextAvailableError('Date is required');
      setDisabled(false);
      return;
    }

    e.preventDefault();
    e.stopPropagation();

    let data = {
      PropertyId: parseInt(propertyID),
      LettingNextAvail: moment(nextAvailableDate).utc(true),
    };

    const config = {
      method: 'POST',
      url: '/Property/UpdatePropertyLettingNextAvail',
      headers: {
        'Content-Type': 'application/json',
      },
      data: JSON.stringify(data),
    };

    try {
      const data = await axios(config);
      if (data.status === 200 || data.status === 201) {
        setDisabled(false);
        toast.success(`Next Available Date Updated`, {
          position: 'top-center',
          autoClose: 3000,
          hideProgressBar: false,
          closeOnClick: true,
          draggable: true,
          progress: undefined,
        });
        setShowNextAvailableModal(false);
        refetch();
        setShowUpdateOwnerLastContactModalQuestion(true);
      }
    } catch (err) {
      if (err.response?.status === 500) setComment('');
      setDisabled(false);
    }
  };

  useEffect(() => {
    if (!show) return;
    if (!navigator?.geolocation) return;
    const getCurrentLocation = function () {
      return new Promise(function (resolve, reject) {
        navigator.geolocation.getCurrentPosition(resolve, reject);
      });
    };
    getCurrentLocation()
      .then((position) => {
        setCurrentLat(position.coords.latitude);
        setCurrentLon(position.coords.longitude);
      })
      .catch(() => {});
  }, [show]);

  const closeAll = () => {
    setShowFavouriteModal(false);
    setShowPriceModal(false);
    setShowCoordinatesModal(false);
    setShowUploadModal(false);
    setShowUpdateOwnerLastContactModal(false);
    setShowUpdateOwnerLastContactModalQuestion(false);
    setShowNextAvailableModal(false);
    setShowMarketStatusModal(false);
    setErrorImages(null);
    setReason(null);
    setComment(null);
    setPrice(null);
    if (onClose) onClose();
  };

  const openPriceModal = () => {
    if (propertyData?.forSaleOrToLet === false) {
      setPrice(propertyData?.propertyForSale?.price?.toString());
    } else {
      setPrice(propertyData?.propertyToLet?.longLetRate?.toString());
    }
    setShowPriceModal(true);
  };

  const canShowMap = propertyData?.googleMapLatitude != null && propertyData?.googleMapLongitude != null;
  const gallery = propertyData?.externalFiles;
  const isToLet = propertyData?.forSaleOrToLet === true;

  if (!show) return null;

  if (isLoadingPropertyDetails && !propertyData) {
    return (
      <Modal show={show} onHide={closeAll} centered dialogClassName="property-actions-modal">
        <Modal.Body>
          <CenteredSpinner />
        </Modal.Body>
      </Modal>
    );
  }

  return (
    <>
      <Modal show={show} onHide={closeAll} centered size="xl" dialogClassName="property-actions-modal">
        <div className="property-actions-shell">
          <button
            type="button"
            className="property-actions-close"
            aria-label="Close"
            onClick={closeAll}
          >
            ×
          </button>
          <div className="property-actions-hero">
            <div className="d-flex align-items-center justify-content-between">
              <div>
                <div className="badge badge-light text-uppercase">
                  {propertyData?.forSaleOrToLet === false ? 'For Sale' : 'To Let'}
                </div>
                <h5 className="mb-1 mt-2">{propertyData?.shortDescription || propertyData?.propertyTypeName}</h5>
                <p className="mb-0 text-muted text-capitalize">
                  {propertyData?.localityName}
                  {propertyData?.addressLine1 ? ` • ${propertyData?.addressLine1}` : ''}
                </p>
              </div>
              <div className="text-right">
                <div className="text-muted small">Ref {propertyData?.propertyRef}</div>
                <div className="h4 mb-0">
                  {propertyData?.forSaleOrToLet === false
                    ? numberWithCommas(propertyData?.propertyForSale?.price)
                    : numberWithCommas(propertyData?.propertyToLet?.longLetRate) + ' ' + propertyData?.propertyToLet?.leaseRatePeriod}
                </div>
              </div>
            </div>
            <div className="property-actions-quick">
              {isToLet && propertyData?.propertyToLet?.lettingNextAvail ? (
                <span className="pill">
                  <Clock size={14} className="mr-1" />{' '}
                  {moment(propertyData.propertyToLet.lettingNextAvail).format('DD/MM/YYYY')}
                </span>
              ) : null}
              <span className="pill">
                <i className="fas fa-bed mr-1"></i>
                {propertyData?.bedrooms ?? '-'}
              </span>
              {propertyData?.areaSqMtrs ? (
                <span className="pill">
                  <i className="fas fa-expand mr-1"></i>
                  {propertyData?.areaSqMtrs} SqMtrs
                </span>
              ) : null}
              <span className="pill">
                {propertyData?.marketStatusCode || 'Not set'}
              </span>
              <button
                type="button"
                className="pill pill-button"
                onClick={() => setShowMarketStatusModal(true)}
                title="Update market status"
                aria-label="Update market status"
              >
                Update
              </button>
            </div>
          </div>

          <div className="property-actions-body">
            <div className="property-actions-gallery">
              {gallery && gallery.length > 0 ? (
                <Swiper
                  slidesPerView={1}
                  navigation
                  pagination={{
                    dynamicBullets: true,
                  }}
                >
                  {(gallery || []).map((file, index) => {
                    const url = `${AppConstants.BASE_URL_FILE + file.filePath}`;
                    return (
                      <SwiperSlide key={index}>
                        <div className="property-actions-image">
                          <img src={url} alt="Property" className="img-fluid w-100" />
                          <a href={url} target="_blank" rel="noreferrer" className="property-actions-open-link">
                            Open
                          </a>
                        </div>
                      </SwiperSlide>
                    );
                  })}
                </Swiper>
              ) : (
                <img src={noImage} alt="defaultImage" className="img-fluid w-100 rounded" />
              )}
            </div>
            <div className="property-actions-sidebar">
              <div className="property-actions-grid">
                {canShowMap ? (
                  <button
                    className="property-actions-chip"
                    onClick={() => handleMap(propertyData, originPath || path)}
                    title="Open map"
                  >
                    <Map size={18} />
                    <span>Map</span>
                  </button>
                ) : null}

                {canShowMap ? (
                  <a
                    className="property-actions-chip"
                    href={
                      'https://www.google.com/maps/search/?api=1&query=' +
                      propertyData?.googleMapLatitude +
                      ',' +
                      propertyData?.googleMapLongitude
                    }
                    target="_blank"
                    rel="noreferrer"
                    title="Open in Google Maps"
                  >
                    <NavigationIcon size={18} />
                    <span>Navigate</span>
                  </a>
                ) : null}

                {path !== '/Favourites' && path !== '/HotProperties' ? (
                  <button
                    className="property-actions-chip"
                    onClick={(e) => {
                      setShowFavouriteModal(true);
                      e.stopPropagation();
                    }}
                    title="Add to favourites"
                  >
                    <Heart size={18} />
                    <span>Favourite</span>
                  </button>
                ) : null}

                <button className="property-actions-chip" onClick={handleAddBuyerProperties} title="Add to buyer">
                  <UserPlus size={18} />
                  <span>Add buyer</span>
                </button>

                <button className="property-actions-chip" onClick={() => setShowUploadModal(true)} title="Upload images">
                  <Image size={18} />
                  <span>Upload</span>
                </button>

                <button
                  className="property-actions-chip"
                  onClick={() => setShowCoordinatesModal(true)}
                  title="Update coordinates"
                >
                  <MapPin size={18} />
                  <span>Set coords</span>
                </button>

                <button className="property-actions-chip" onClick={openPriceModal} title="Update price">
                  <span>€ Price</span>
                </button>

                {propertyData?.publishToWebsite === true && propertyData?.showingOnWeb === true ? (
                  <a
                    className="property-actions-chip"
                    href={`https://wa.me/?text=${encodeURI(
                      getSysSetting(AppConstants.SysSettingId_PropertyWebUrl)?.stringValue + propertyData.propertyRef,
                    )}`}
                    target={'_blank'}
                    rel="noreferrer"
                    title="Share website"
                  >
                    <Share2 size={18} />
                    <span>Share web</span>
                  </a>
                ) : null}

                {propertyData?.webReportUrl ? (
                  <a
                    className="property-actions-chip"
                    href={`https://wa.me/?text=${encodeURI(propertyData.webReportUrl)}`}
                    target={'_blank'}
                    rel="noreferrer"
                    title="Share report"
                  >
                    <FileText size={18} />
                    <span>Report</span>
                  </a>
                ) : null}

                <button
                  className="property-actions-chip"
                  onClick={() => setShowUpdateOwnerLastContactModal(true)}
                  title="Owner last contact"
                >
                  <Calendar size={18} />
                  <span>Last contact</span>
                </button>

                {isToLet ? (
                  <button
                    className="property-actions-chip"
                    onClick={() => {
                      setNextAvailableError(null);
                      setShowNextAvailableModal(true);
                    }}
                    title="Next available date"
                  >
                    <Calendar size={18} />
                    <span>Availability</span>
                  </button>
                ) : null}
              </div>

              <div className="property-actions-meta">
                <div className="property-actions-meta-item">
                  <span className="label">Address</span>
                  <p className="mb-1">{propertyData?.addressLine1}</p>
                  <p className="mb-1">{propertyData?.addressLine2}</p>
                  <p className="mb-1">{propertyData?.addressLine3}</p>
                </div>
                {propertyData?.internalComments ? (
                  <div className="property-actions-meta-item">
                    <span className="label">Internal comments</span>
                    <p
                      className="mb-0 small break-word"
                      dangerouslySetInnerHTML={{
                        __html: propertyData.internalComments,
                      }}
                    ></p>
                  </div>
                ) : null}
                <div className="property-actions-meta-item d-flex align-items-center justify-content-between">
                  <div>
                    <span className="label d-block">Owner last contact</span>
                    <Moment format="DD/MM/YYYY">{propertyData?.ownerLastContacted}</Moment>
                  </div>
                  <Button variant="light" size="sm" className="btn-update-owner" onClick={() => setShowUpdateOwnerLastContactModal(true)}>
                    Update
                  </Button>
                </div>
              </div>
            </div>
          </div>

          <div className="property-actions-footer text-right">
            <Button variant="outline-secondary" onClick={closeAll}>
              Close
            </Button>
          </div>
        </div>
      </Modal>

      <Modal show={showFavouriteModal} backdrop="static" centered>
        <Modal.Header
          closeButton
          onClick={() => {
            setReason(null);
            setReasonError(null);
            setCommentError(null);
            setShowFavouriteModal(false);
          }}
        >
          <Modal.Title>
            <span className="font-weight-bold">Add To Favourites</span>
          </Modal.Title>
        </Modal.Header>
        <Modal.Body>
          <label className="form-label mb-2" htmlFor="reason">
            Reason:
          </label>
          <Select
            id="reason"
            className={`${reasonError && 'react-select'}`}
            classNamePrefix={`${reasonError && 'react-select'}`}
            onChange={handleReasonChange}
            isSearchable={true}
            options={reasonOptions}
          />
          {reasonError && (
            <small className="text-danger" style={{ display: 'block' }}>
              {reasonError}
            </small>
          )}
          <label className="form-label block mb-2 mt-2" htmlFor="comments">
            Comments:
          </label>
          <input
            id="comments"
            autoComplete="off"
            className={`form-control d-block w-100 ${commentError && 'border-danger'}`}
            type="text"
            value={comment ?? ''}
            onChange={handleComment}
            name="comment"
            required
          />
          {commentError && <small className="text-danger">{commentError}</small>}
        </Modal.Body>
        <Modal.Footer>
          <Button
            variant={comment !== null && disabled === true ? 'success' : 'primary'}
            onClick={(event) => addFavourite(event, propertyData?.propertyRef)}
            disabled={comment !== null && disabled === true}
          >
            {comment !== null && disabled === true ? 'Adding...' : 'Add'}
          </Button>
        </Modal.Footer>
      </Modal>

      <OwnerLastContactModal
        showModal={showUpdateOwnerLastContactModal}
        noteTextError={noteTextError}
        setNoteText={setNoteText}
        setNoteTextError={setNoteTextError}
        setShowModal={setShowUpdateOwnerLastContactModal}
        ownerContactDate={ownerContactDate}
        setOwnerContactDate={setOwnerContactDate}
        ownerContactDateError={ownerContactDateError}
        disabled={disabled}
        updateOwnerLastContact={handleUpdateOwnerLastContact}
      />

      <Modal show={showUpdateOwnerLastContactModalQuestion} backdrop="static" centered>
        <Modal.Header
          closeButton
          onClick={() => {
            setShowUpdateOwnerLastContactModalQuestion(false);
          }}
        >
          <Modal.Title>
            <span className="font-weight-bold">Owner Contacted Today</span>
          </Modal.Title>
        </Modal.Header>
        <Modal.Body>
          <label className="form-label block mb-2 mt-2" htmlFor="NoteText">
            Have you contacted the owner of the property?
          </label>
          {noteTextError && <small className="text-danger">{noteTextError}</small>}
        </Modal.Body>
        <Modal.Footer>
          <Button
            variant="danger"
            onClick={() => {
              setShowUpdateOwnerLastContactModalQuestion(false);
            }}
          >
            No
          </Button>
          <Button
            variant={noteText !== null && disabled === true ? 'success' : 'primary'}
            onClick={(event) => {
              handleUpdateOwnerLastContactQuestion(event);
            }}
          >
            {noteText !== null && disabled === true ? 'Updating...' : 'Yes'}
          </Button>
        </Modal.Footer>
      </Modal>

      <Modal show={showCoordinatesModal} backdrop="static" centered>
        <Modal.Header
          closeButton
          onClick={() => {
            setShowCoordinatesModal(false);
          }}
        >
          <Modal.Title>
            <span className="font-weight-bold">Update Property Location</span>
          </Modal.Title>
        </Modal.Header>
        <Modal.Body>
          <label className="form-label block mb-2 mt-2">
            Are you sure you want to set current location as property coordinates?
          </label>
        </Modal.Body>
        <Modal.Footer>
          <Button
            variant="danger"
            onClick={() => {
              setShowCoordinatesModal(false);
            }}
          >
            No
          </Button>
          <Button
            variant="primary"
            onClick={(event) => {
              handleUpdatePropertyCoordinates(event);
            }}
          >
            Yes
          </Button>
        </Modal.Footer>
      </Modal>

      <Modal show={showPriceModal} backdrop="static" centered>
        <Modal.Header
          closeButton
          onClick={() => {
            setPrice(null);
            setShowPriceModal(false);
          }}
        >
          <Modal.Title>
            <span className="font-weight-bold">Update Property Price</span>
          </Modal.Title>
        </Modal.Header>
        <Modal.Body>
          <label className="form-label block mb-2 mt-2" htmlFor="price">
            New price:
          </label>
          <input
            id="price"
            autoComplete="off"
            className={`form-control d-block w-100 ${commentError && 'border-danger'}`}
            type="text"
            value={price ?? ''}
            onChange={handlePriceChange}
            onBlur={handlePriceAutoComplete}
            name="price"
            placeholder="100k"
            required
          />
          {commentError && <small className="text-danger">{commentError}</small>}
        </Modal.Body>
        <Modal.Footer>
          <Button
            variant="danger"
            onClick={() => {
              setShowPriceModal(false);
            }}
          >
            Cancel
          </Button>
          <Button
            variant="primary"
            onClick={(event) => {
              handleUpdatePropertyPrice(event);
            }}
          >
            Confirm
          </Button>
        </Modal.Footer>
      </Modal>

      <Modal show={showUploadModal} centered backdrop="static">
        <Modal.Header
          closeButton
          onClick={() => {
            setShowUploadModal(false);
            setErrorImages(null);
          }}
        >
          <Modal.Title>
            <span className="font-weight-bold">Upload Property Images</span>
          </Modal.Title>
        </Modal.Header>
        <Modal.Body>
          {images !== null ? (
            <>
              <div className="grid-container-images">
                {(images || []).map((url, index) => (
                  <img
                    key={index}
                    src={url}
                    alt="property images"
                    height="200px"
                    style={{ objectFit: 'cover' }}
                    className=""
                    onClick={() => handleDeleteImage(index)}
                  />
                ))}
              </div>
              <br />
              {images !== null && images.length > 0 ? (
                <button
                  onClick={() => {
                    setImages(null);
                    setMulitpleFiles(null);
                    setErrorImages(null);
                  }}
                  className="mt-2 mb-2 btn btn-primary"
                >
                  {images.length > 1 ? 'Remove all images' : 'Remove image'}
                </button>
              ) : null}
            </>
          ) : null}
          <p>{images?.length > 0 ? errorImages && errorImages.type : null}</p>
          <p>{images?.length > 0 ? errorImages && errorImages.size : null}</p>
          <br />
          <div className="form-group">
            <input
              type="file"
              className="form-control"
              onChange={(e) => handleUploadMultipleFiles(e)}
              accept="image/*"
              multiple
              ref={refImage}
            />
          </div>
        </Modal.Body>
        <Modal.Footer>
          <Button
            variant="danger"
            onClick={() => {
              setShowUploadModal(false);
            }}
          >
            Cancel
          </Button>
          {multipleFiles !== null && multipleFiles.length > 0 ? (
            <button
              onClick={() => {
                handleUploadPropertyImages();
              }}
              className="mt-2 mb-2 btn btn-primary"
            >
              {disabled === true ? 'Saving...' : 'Save'}
            </button>
          ) : null}
        </Modal.Footer>
      </Modal>

      <UpdateNextAvailableDateModal
        showModal={showNextAvailableModal}
        setNextAvailableError={setNextAvailableError}
        setShowModal={setShowNextAvailableModal}
        nextAvailableDate={nextAvailableDate}
        setNextAvailableDate={setNextAvailableDate}
        nextAvailableError={nextAvailableError}
        disabled={disabled}
        updateNextAvailableDate={handleUpdateNextAvailableDate}
      />

      <UpdateMarketStatusModal
        showModal={showMarketStatusModal}
        setShowModal={setShowMarketStatusModal}
        propertyId={propertyID}
        currentStatusCode={propertyData?.marketStatusCode}
        onSuccess={refetch}
      />
    </>
  );
};

